/**
 * Injects a partner's raw `@font-face` CSS into `document.head` the first time it's called with a
 * given id, and is a no-op on every subsequent call — mirrors the dedupe idiom in
 * `PresentationNext/util/googleFonts/loadGoogleFontFamily.ts`'s `loadStylesheet`, but for
 * Cloudinary-hosted `@font-face` text instead of a Google Fonts stylesheet href.
 *
 * Deliberately synchronous (no fetch, no Promise): these partner font-face rules used to live in
 * the global index.css, present in the document before React ever rendered. The PDF renderer's
 * `document.fonts.status` wait (apps/api render-with-browserless.ts) and the editor's
 * `getParentFontFaceCss` iframe scrape both depend on the rule already being registered in
 * `document.styleSheets` by the time they run — a synchronous `<style>` append called at module
 * scope (import time) preserves that ordering without needing either caller to change.
 *
 * No-op outside a browser: callers run at module scope, so a non-DOM import of a partner section
 * must not throw on a missing `document`.
 */
export function injectPartnerFontFace(id: string, css: string, doc?: Document): void {
  const target = doc ?? (typeof document === 'undefined' ? undefined : document)
  if (!target || target.getElementById(id)) {
    return
  }
  const style = target.createElement('style')
  style.id = id
  style.appendChild(target.createTextNode(css))
  target.head.appendChild(style)
}
